import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../environment";
import { getAuthHeaders } from "../utils/authUtils";
import { getLifecycleTypeName } from "../utils/lifecycleUtils";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import "./TaskDetailModal.css";

interface TaskDetails {
  task_id: number;
  title: string;
  category: string;
  start_date: string | null;
  due_date: string | null;
  priority: string;
  status: string;
  description: string;
  create_meeting?: boolean;
  attendees?: string[];
  created_datetime: string;
  related_entity_name: string;
  related_entity_id: number;
  related_entity_title?: string;
  responsible_persons: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  responsible_team: {
    team_id: number;
    name: string;
  };
  created_by: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  tenant_id: number;
  tenant: {
    tenant_id: number;
    tenant_name: string;
  };
}

interface User {
  user_id: number;
  tenant_user_id: number;
  user_first_name: string;
  user_last_name: string;
  email: string;
  avatar_url?: string;
  role_display_name: string;
  is_active: boolean;
  tenant_id: number;
}

interface Team {
  team_id: number;
  name: string;
}

interface TeamMember {
  team_user_id: number;
  user_full_name: string;
  team_id: number;
  user_id: number;
}

interface TaskFormData {
  title: string;
  responsible_person: number | null;
  assigned_team: number | null;
  start_date: Date | null;
  due_date: Date | null;
  category: string;
  priority: string;
  status: string;
  description: string;
  create_meeting: boolean;
  attendees: string[];
}

interface TaskDetailModalProps {
  isVisible: boolean;
  taskDetails: TaskDetails | null;
  isLoading: boolean;
  onClose: () => void;
  onTaskUpdate?: () => void;
  tenantId?: number | string | null;
}

const EMPTY_FORM_DATA: TaskFormData = {
  title: "",
  responsible_person: null,
  assigned_team: null,
  start_date: null,
  due_date: null,
  category: "",
  priority: "medium",
  status: "pending",
  description: "",
  create_meeting: false,
  attendees: [],
};

const buildFormDataFromTask = (task: TaskDetails): TaskFormData => ({
  title: task.title,
  responsible_person: task.responsible_persons?.user_id || null,
  assigned_team: task.responsible_team?.team_id || null,
  start_date: task.start_date ? new Date(task.start_date) : null,
  due_date: task.due_date ? new Date(task.due_date) : null,
  category: task.category,
  priority: task.priority,
  status: task.status,
  description: task.description,
  create_meeting: task.create_meeting || false,
  attendees: task.attendees || [],
});

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  isVisible,
  taskDetails,
  isLoading,
  onClose,
  onTaskUpdate,
  tenantId,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [editFormData, setEditFormData] = useState<TaskFormData>(EMPTY_FORM_DATA);
  const [originalFormData, setOriginalFormData] = useState<TaskFormData | null>(null);
  const [dateValidationError, setDateValidationError] = useState<string>("");

  const priorityOptions = [
    {
      value: "high",
      label: "High",
      icon: <span className="badge bg-danger rounded-circle me-2">&nbsp;</span>,
    },
    {
      value: "medium",
      label: "Medium",
      icon: (
        <span className="badge bg-warning rounded-circle me-2">&nbsp;</span>
      ),
    },
    {
      value: "low",
      label: "Low",
      icon: (
        <span className="badge bg-success rounded-circle me-2">&nbsp;</span>
      ),
    },
  ];

  const statusOptions = [
    {
      value: "pending",
      label: "Pending",
      icon: <span className="badge bg-info rounded-circle me-2">&nbsp;</span>,
    },
    {
      value: "in progress",
      label: "In Progress",
      icon: (
        <span className="badge bg-warning rounded-circle me-2">&nbsp;</span>
      ),
    },
    {
      value: "completed",
      label: "Completed",
      icon: (
        <span className="badge bg-success rounded-circle me-2">&nbsp;</span>
      ),
    },
  ];

  const categoryOptions = [
    { value: "Calls", label: "Calls" },
    { value: "Email", label: "Email" },
    { value: "Meeting", label: "Meeting" },
    { value: "Follow-up", label: "Follow-up" },
    { value: "Research", label: "Research" },
    { value: "Administrative", label: "Administrative" },
  ];

  const selectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: 42,
      borderRadius: 10,
      borderColor: state.isFocused ? "#824eeb" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(130, 78, 235, 0.12)" : "none",
      "&:hover": { borderColor: "#824eeb" },
    }),
    menu: (base: any) => ({
      ...base,
      borderRadius: 10,
      overflow: "hidden",
      zIndex: 20,
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#824eeb"
        : state.isFocused
        ? "#f9f5ff"
        : "white",
      color: state.isSelected ? "white" : "#344054",
    }),
  };

  const formatTaskDate = (dateString: string | null): string => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const validateDates = (
    startDate: Date | null,
    dueDate: Date | null
  ): string => {
    if (startDate && dueDate && startDate > dueDate) {
      return "Start date cannot be after due date";
    }
    return "";
  };

  const fetchUsers = useCallback(async () => {
    if (!tenantId) return;
    try {
      const endpoint = `${API_BASE_URL}/tenant-user/?tenant_id=${tenantId}`;
      const response = await axios.get(endpoint, {
        headers: getAuthHeaders(),
      });
      setUsers(response.data.results || response.data);
    } catch (err: any) {
      console.error("Error loading users:", err);
    }
  }, [tenantId]);

  const fetchTeams = useCallback(async () => {
    if (!tenantId) return;
    try {
      const endpoint = `${API_BASE_URL}/team/?tenant_id=${tenantId}`;
      const response = await axios.get(endpoint, {
        headers: getAuthHeaders(),
      });
      setTeams(response.data.results || response.data);
    } catch (err: any) {
      console.error("Error loading teams:", err);
    }
  }, [tenantId]);

  const fetchTeamMembers = useCallback(async (teamId: number) => {
    if (!teamId) {
      setTeamMembers([]);
      return;
    }
    try {
      const endpoint = `${API_BASE_URL}/team-user/?team_id=${teamId}`;
      const response = await axios.get(endpoint, {
        headers: getAuthHeaders(),
      });
      setTeamMembers(response.data.results || response.data);
    } catch (err: any) {
      console.error("Error loading team members:", err);
      setTeamMembers([]);
    }
  }, []);

  const handleInputChange = (field: keyof TaskFormData, value: any) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));

    if (field === "assigned_team") {
      if (value) {
        fetchTeamMembers(value);
      } else {
        setTeamMembers([]);
      }
      setEditFormData((prev) => ({ ...prev, responsible_person: null }));
    }
  };

  const handleStartDateChange = (date: Date | null) => {
    handleInputChange("start_date", date);
    const dateError = validateDates(date, editFormData.due_date);
    setDateValidationError(dateError);
  };

  const handleDueDateChange = (date: Date | null) => {
    handleInputChange("due_date", date);
    const dateError = validateDates(editFormData.start_date, date);
    setDateValidationError(dateError);
  };

  const isFormValid = () => {
    return (
      editFormData.title.trim() !== "" &&
      editFormData.start_date !== null &&
      editFormData.due_date !== null &&
      editFormData.category.trim() !== "" &&
      editFormData.priority.trim() !== "" &&
      editFormData.status.trim() !== "" &&
      editFormData.description.trim() !== "" &&
      dateValidationError === ""
    );
  };

  const isDirty = useMemo(() => {
    if (!originalFormData) return false;
    const startChanged =
      (editFormData.start_date?.getTime() ?? null) !==
      (originalFormData.start_date?.getTime() ?? null);
    const dueChanged =
      (editFormData.due_date?.getTime() ?? null) !==
      (originalFormData.due_date?.getTime() ?? null);

    return (
      editFormData.title !== originalFormData.title ||
      editFormData.responsible_person !== originalFormData.responsible_person ||
      editFormData.assigned_team !== originalFormData.assigned_team ||
      startChanged ||
      dueChanged ||
      editFormData.category !== originalFormData.category ||
      editFormData.priority !== originalFormData.priority ||
      editFormData.status !== originalFormData.status ||
      editFormData.description !== originalFormData.description ||
      editFormData.create_meeting !== originalFormData.create_meeting ||
      JSON.stringify(editFormData.attendees) !==
        JSON.stringify(originalFormData.attendees)
    );
  }, [editFormData, originalFormData]);

  const handleSaveEdit = async () => {
    if (!taskDetails || !isFormValid() || !isDirty) return;

    setIsUpdating(true);
    try {
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

      const payload = {
        title: editFormData.title,
        responsible_persons: editFormData.responsible_person,
        responsible_team: editFormData.assigned_team,
        start_date: editFormData.start_date
          ? editFormData.start_date.toISOString()
          : null,
        due_date: editFormData.due_date
          ? editFormData.due_date.toISOString()
          : null,
        category: editFormData.category,
        priority: editFormData.priority,
        status: editFormData.status,
        description: editFormData.description,
        create_meeting: editFormData.create_meeting,
        attendees: editFormData.attendees,
        tenant_id: tenantId,
        updated_by: currentUser.user_id || currentUser.id || currentUser.userId,
        related_entity_id: taskDetails.related_entity_id,
        related_entity_name: taskDetails.related_entity_name,
      };

      await axios.put(`${API_BASE_URL}/task/${taskDetails.task_id}/`, payload, {
        headers: getAuthHeaders(),
      });

      setDateValidationError("");
      setOriginalFormData(editFormData);

      if (onTaskUpdate) {
        onTaskUpdate();
      }

      Swal.fire({
        title: "Success!",
        text: "Task updated successfully!",
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
      });
    } catch (err: any) {
      console.error("Error updating task:", err);
      Swal.fire({
        title: "Error!",
        text: "Failed to update task. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#d33",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const createEntityUrl = (task: TaskDetails): string => {
    if (!task.related_entity_id) return "#";

    const entityName = task.related_entity_name.toLowerCase();
    switch (entityName) {
      case "lead":
        return `/leads/${task.related_entity_id}`;
      case "deal":
        return `/deals/${task.related_entity_id}`;
      case "contact":
        return `/contacts/${task.related_entity_id}`;
      case "company":
        return `/companies/${task.related_entity_id}`;
      case "pipeline":
        return `/pipelines/${task.related_entity_id}`;
      default:
        return `/${task.related_entity_name}/${task.related_entity_id}`;
    }
  };

  const createEntityListUrl = (entityName: string): string => {
    const name = entityName.toLowerCase();
    switch (name) {
      case "lead":
        return "/leads";
      case "deal":
        return "/deals";
      case "contact":
        return "/contacts";
      case "company":
        return "/companies";
      case "pipeline":
        return "/pipelines";
      default:
        return `/${name}s`;
    }
  };

  useEffect(() => {
    if (isVisible && tenantId) {
      fetchUsers();
      fetchTeams();
    }
  }, [isVisible, tenantId, fetchUsers, fetchTeams]);

  useEffect(() => {
    if (isVisible && taskDetails) {
      const formData = buildFormDataFromTask(taskDetails);
      setEditFormData(formData);
      setOriginalFormData(formData);
      setDateValidationError("");
      if (taskDetails.responsible_team?.team_id) {
        fetchTeamMembers(taskDetails.responsible_team.team_id);
      } else {
        setTeamMembers([]);
      }
    }
  }, [isVisible, taskDetails, fetchTeamMembers]);

  if (!isVisible) return null;

  return (
    <>
      <div
        className="modal fade show"
        style={{ display: "block", marginLeft: "8%" }}
        role="dialog"
        onClick={onClose}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: "70vw", width: "70vw" }}
        >
          <div
            className="modal-content tdm-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tdm-header">
              <div className="tdm-icon-badge">
                <i className="ti ti-clipboard-check" />
              </div>
              <h4 className="tdm-title">Task Details</h4>
              <button
                type="button"
                className="tdm-close-btn"
                onClick={onClose}
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div
              className="modal-body tdm-body"
              style={{ height: "500px", overflowY: "auto" }}
            >
              {isLoading ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ height: "100%" }}
                >
                  <div className="text-center">
                    <div
                      className="spinner-border text-primary"
                      role="status"
                      style={{ width: "3rem", height: "3rem" }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 mb-0">Loading task details...</p>
                  </div>
                </div>
              ) : taskDetails ? (
                <div className="container-fluid">
                  {taskDetails.related_entity_name && (
                    <div className="row mb-4">
                      <div className="col-12">
                        <nav aria-label="breadcrumb">
                          <ol
                            className="breadcrumb mb-0 tdm-breadcrumb"
                            style={{
                              padding: "0.75rem 1rem",
                              fontWeight: "bold",
                            }}
                          >
                            <li className="breadcrumb-item">
                              <Link
                                to={createEntityListUrl(
                                  taskDetails.related_entity_name
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: "#824eeb",
                                  textDecoration: "none",
                                  fontWeight: "bold",
                                }}
                              >
                                {taskDetails.related_entity_name ===
                                  "proposal" ||
                                taskDetails.related_entity_name === "contract"
                                  ? taskDetails.related_entity_name
                                      .charAt(0)
                                      .toUpperCase() +
                                    taskDetails.related_entity_name.slice(1)
                                  : getLifecycleTypeName(
                                      taskDetails.related_entity_name.toUpperCase()
                                    )}
                              </Link>
                            </li>
                            {taskDetails.related_entity_title && (
                              <li className="breadcrumb-item">
                                <Link
                                  to={createEntityUrl(taskDetails)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: "#824eeb",
                                    textDecoration: "none",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {taskDetails.related_entity_title}
                                </Link>
                              </li>
                            )}
                            <li
                              className="breadcrumb-item active"
                              aria-current="page"
                              style={{
                                color: "#6b7280",
                                fontWeight: "500",
                              }}
                            >
                              {taskDetails.title}
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>
                  )}

                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editFormData.title ?? ""}
                        onChange={(e) =>
                          handleInputChange("title", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-bold">Category</label>
                      <Select
                        options={categoryOptions}
                        value={
                          categoryOptions.find(
                            (option) => option.value === editFormData.category
                          ) || null
                        }
                        onChange={(option) =>
                          handleInputChange("category", option?.value || "")
                        }
                        placeholder="Select category"
                        className="basic-single"
                        styles={selectStyles}
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label fw-bold">Priority</label>
                      <Select
                        options={priorityOptions}
                        value={
                          priorityOptions.find(
                            (option) => option.value === editFormData.priority
                          ) || priorityOptions[1]
                        }
                        onChange={(option) =>
                          handleInputChange(
                            "priority",
                            option?.value || "medium"
                          )
                        }
                        className="basic-single"
                        styles={selectStyles}
                        formatOptionLabel={(option: any) => (
                          <div className="d-flex align-items-center">
                            {option.icon}
                            <span>{option.label}</span>
                          </div>
                        )}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Status</label>
                      <Select
                        options={statusOptions}
                        value={
                          statusOptions.find(
                            (option) => option.value === editFormData.status
                          ) || statusOptions[0]
                        }
                        onChange={(option) =>
                          handleInputChange(
                            "status",
                            option?.value || "pending"
                          )
                        }
                        className="basic-single"
                        styles={selectStyles}
                        formatOptionLabel={(option: any) => (
                          <div className="d-flex align-items-center">
                            {option.icon}
                            <span>{option.label}</span>
                          </div>
                        )}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Start Date</label>
                      <DatePicker
                        selected={editFormData.start_date}
                        onChange={handleStartDateChange}
                        className="form-control"
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        timeCaption="Time"
                        dateFormat="MMM dd, yyyy, h:mm aa"
                        placeholderText="Select start date & time"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Due Date</label>
                      <DatePicker
                        selected={editFormData.due_date}
                        onChange={handleDueDateChange}
                        className="form-control"
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        timeCaption="Time"
                        dateFormat="MMM dd, yyyy, h:mm aa"
                        placeholderText="Select due date & time"
                      />
                    </div>
                  </div>

                  {dateValidationError && (
                    <div className="row mb-3">
                      <div className="col-12">
                        <div className="alert alert-danger">
                          {dateValidationError}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label fw-bold">
                        Assigned Team
                      </label>
                      <Select
                        options={teams.map((team) => ({
                          value: team.team_id,
                          label: team.name,
                        }))}
                        value={
                          editFormData.assigned_team
                            ? {
                                value: editFormData.assigned_team,
                                label: teams.find(
                                  (t) =>
                                    t.team_id === editFormData.assigned_team
                                )?.name,
                              }
                            : null
                        }
                        onChange={(option) =>
                          handleInputChange(
                            "assigned_team",
                            option?.value || null
                          )
                        }
                        placeholder="Select team first"
                        isClearable
                        className="basic-single"
                        styles={selectStyles}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-bold">
                        Responsible Person
                      </label>
                      <Select
                        options={
                          teamMembers.length > 0
                            ? teamMembers.map((member: any) => ({
                                value:
                                  member.user_id ||
                                  member.user?.user_id ||
                                  member.user?.id ||
                                  member.id ||
                                  member.team_user_id,
                                label:
                                  member.user_full_name ||
                                  member.user?.full_name ||
                                  member.user?.first_name ||
                                  "Unknown User",
                              }))
                            : users.map((user) => ({
                                value: user.user_id,
                                label: `${user.user_first_name} ${user.user_last_name}`,
                              }))
                        }
                        value={
                          editFormData.responsible_person
                            ? {
                                value: editFormData.responsible_person,
                                label:
                                  teamMembers.find(
                                    (m) =>
                                      m.user_id ===
                                      editFormData.responsible_person
                                  )?.user_full_name ||
                                  users.find(
                                    (u) =>
                                      u.user_id ===
                                      editFormData.responsible_person
                                  )?.user_first_name +
                                    " " +
                                    users.find(
                                      (u) =>
                                        u.user_id ===
                                        editFormData.responsible_person
                                    )?.user_last_name,
                              }
                            : null
                        }
                        onChange={(option) =>
                          handleInputChange(
                            "responsible_person",
                            option?.value || null
                          )
                        }
                        placeholder={
                          editFormData.assigned_team
                            ? "Select responsible person"
                            : "Select team first"
                        }
                        isClearable
                        className="basic-single"
                        styles={selectStyles}
                        isDisabled={!editFormData.assigned_team}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Created At</label>
                      <input
                        type="text"
                        className="form-control"
                        value={
                          formatTaskDate(taskDetails.created_datetime) ?? ""
                        }
                        disabled
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-12">
                      <label className="form-label fw-bold">Description</label>
                      <textarea
                        className="form-control"
                        rows={4}
                        value={editFormData.description ?? ""}
                        onChange={(e) =>
                          handleInputChange("description", e.target.value)
                        }
                        style={{ resize: "none" }}
                      />
                    </div>
                  </div>

                  {taskDetails.category === "Meeting" &&
                    taskDetails.attendees &&
                    taskDetails.attendees.length > 0 && (
                      <div className="row mb-3">
                        <div className="col-md-12">
                          <label className="form-label fw-bold">Attendees</label>
                          <div className="d-flex flex-wrap gap-2">
                            {taskDetails.attendees.map((email, index) => (
                              <span key={index} className="badge bg-primary">
                                {email}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              ) : (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ height: "100%" }}
                >
                  <p className="text-muted">No task details available.</p>
                </div>
              )}
            </div>
            <div className="modal-footer tdm-footer">
              <button
                type="button"
                className="tdm-btn tdm-btn-secondary"
                onClick={onClose}
                disabled={isUpdating}
              >
                Close
              </button>
              <button
                type="button"
                className="tdm-btn tdm-btn-primary"
                onClick={handleSaveEdit}
                disabled={isUpdating || !isDirty || !isFormValid()}
              >
                {isUpdating ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ti ti-check me-1"></i>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-backdrop fade show tdm-backdrop" onClick={onClose} />
    </>
  );
};

export default TaskDetailModal;
